import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  BusinessRounded,
  CheckCircleRounded,
  LocationOnOutlined,
  SearchRounded,
  WarningAmberRounded,
} from '@mui/icons-material';
import api from '../api/api';

const GENERIC_RUT = '66.666.666-6';

function text(value) {
  return String(value ?? '').trim();
}

function normalizeRut(value) {
  return text(value).replace(/\./g, '').toUpperCase();
}

function clienteNombre(cliente = {}) {
  return text(cliente.razon_social) ||
    [cliente.nombres, cliente.apellidos].map(text).filter(Boolean).join(' ') ||
    text(cliente.nombre) ||
    text(cliente.rut) ||
    'Cliente';
}

function direccionFactura(cliente = {}) {
  if (cliente.direccion_facturacion && typeof cliente.direccion_facturacion === 'object') {
    return cliente.direccion_facturacion;
  }

  if (cliente.direccion_principal && typeof cliente.direccion_principal === 'object') {
    return cliente.direccion_principal;
  }

  const direcciones = Array.isArray(cliente.direcciones) ? cliente.direcciones : [];
  return direcciones.find((direccion) => {
    const tipo = text(direccion?.tipo).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    return tipo === 'facturacion';
  }) || direcciones.find((direccion) => text(direccion?.tipo).toLowerCase() === 'principal') || direcciones[0] || null;
}

function direccionLabel(direccion = {}) {
  if (!direccion) return '';
  const calleNumero = [text(direccion.calle), text(direccion.numero)].filter(Boolean).join(' ');
  const principal = text(direccion.direccion_formateada) || calleNumero;
  return [principal, text(direccion.comuna), text(direccion.ciudad)].filter(Boolean).join(', ');
}

export function validarClienteFactura(cliente) {
  if (!cliente || typeof cliente !== 'object') {
    return { valido: false, faltantes: ['cliente'], direccion: null };
  }

  const faltantes = [];
  const rut = normalizeRut(cliente.rut || cliente.rut_cliente || cliente.rutCliente);
  const genericRut = normalizeRut(GENERIC_RUT);
  const giro = text(cliente.giro);
  const direccion = direccionFactura(cliente);
  const direccionTexto = direccionLabel(direccion);
  const comuna = text(direccion?.comuna || cliente.comuna || cliente.cliente_comuna);

  if (!rut || !rut.includes('-') || rut === genericRut) faltantes.push('RUT tributario');
  if (!giro) faltantes.push('giro');
  if (!direccionTexto) faltantes.push('dirección de facturación');
  if (!comuna) faltantes.push('comuna');
  if (cliente.activo === false) faltantes.push('cliente activo');

  return {
    valido: faltantes.length === 0,
    faltantes,
    direccion,
    direccionTexto,
    rut,
    giro,
  };
}

function extractClientes(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.clientes)) return data.clientes;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

export default function ClienteFacturaModal({ open, clienteActual = null, onClose, onSelect }) {
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [selectingId, setSelectingId] = useState(null);
  const searchRef = useRef(null);
  const requestRef = useRef(0);

  const actualValidation = useMemo(() => validarClienteFactura(clienteActual), [clienteActual]);

  const buscar = useCallback(async (value) => {
    const q = text(value);
    const requestId = ++requestRef.current;
    setLoading(true);
    setError('');

    try {
      const { data } = await api.get('/ventas_clientes', {
        params: {
          search: q || undefined,
          limit: 25,
          orderBy: q ? 'razon_social' : 'id',
          orderDirection: q ? 'asc' : 'desc',
        },
        timeout: 7000,
      });

      if (requestId !== requestRef.current) return;

      const clientes = extractClientes(data)
        .filter((cliente) => normalizeRut(cliente?.rut) !== normalizeRut(GENERIC_RUT));

      setRows(clientes);
      setHighlightedIndex(0);
    } catch (requestError) {
      if (requestId !== requestRef.current) return;
      setRows([]);
      setError(
        requestError?.response?.data?.message ||
        requestError?.response?.data?.error ||
        'No se pudieron consultar los clientes.'
      );
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    setQuery('');
    setRows([]);
    setError('');
    setHighlightedIndex(0);
    setSelectingId(null);

    const focusTimer = window.setTimeout(() => searchRef.current?.focus(), 80);

    return () => {
      window.clearTimeout(focusTimer);
    };
  }, [buscar, open]);

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setTimeout(() => buscar(query), 280);
    return () => window.clearTimeout(timer);
  }, [buscar, open, query]);

  const seleccionar = useCallback(async (candidate) => {
    const id = Number(candidate?.id);
    if (!Number.isInteger(id) || id <= 0 || selectingId) return;

    setSelectingId(id);
    setError('');

    try {
      const { data } = await api.get(`/ventas_clientes/${id}`, { timeout: 7000 });
      const cliente = data?.cliente || data?.data?.cliente || data || candidate;
      const validation = validarClienteFactura(cliente);

      if (!validation.valido) {
        setError(`Cliente incompleto para Factura: ${validation.faltantes.join(', ')}.`);
        return;
      }

      onSelect?.(cliente);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
        requestError?.response?.data?.error ||
        'No se pudo cargar el cliente seleccionado.'
      );
    } finally {
      setSelectingId(null);
    }
  }, [onSelect, selectingId]);

  const highlighted = rows[highlightedIndex] || null;

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex((current) => Math.min(rows.length - 1, current + 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((current) => Math.max(0, current - 1));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      if (highlighted) seleccionar(highlighted);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={selectingId ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
    >
      <DialogTitle sx={{ pb: 1.1 }}>
        <Stack direction="row" spacing={1.1} alignItems="center">
          <BusinessRounded color="primary" />
          <Box>
            <Typography variant="h6" fontWeight={950}>Cliente para Factura</Typography>
            <Typography variant="body2" color="text.secondary">
              Busque por RUT, razón social, nombre, email o teléfono.
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 1.6, bgcolor: '#f6f8fb' }}>
        <TextField
          inputRef={searchRef}
          fullWidth
          size="small"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ej.: 76.123.456-7 o Comercial Ejemplo"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded color="action" />
              </InputAdornment>
            ),
            endAdornment: loading ? (
              <InputAdornment position="end">
                <CircularProgress size={18} />
              </InputAdornment>
            ) : null,
          }}
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 1.5 } }}
        />

        {actualValidation.valido ? (
          <Alert severity="success" icon={<CheckCircleRounded />} sx={{ mt: 1.2, borderRadius: 1.5 }}>
            Cliente actual: <strong>{clienteNombre(clienteActual)}</strong> · {clienteActual?.rut}
          </Alert>
        ) : null}

        {error ? <Alert severity="warning" sx={{ mt: 1.2, borderRadius: 1.5 }}>{error}</Alert> : null}

        <Box
          sx={{
            mt: 1.2,
            bgcolor: '#fff',
            border: '1px solid #dce4ed',
            borderRadius: 1.5,
            overflow: 'hidden',
            minHeight: 230,
            maxHeight: 360,
            overflowY: 'auto',
          }}
        >
          {!loading && !rows.length ? (
            <Box sx={{ minHeight: 230, display: 'grid', placeItems: 'center', p: 3, textAlign: 'center' }}>
              <Box>
                <BusinessRounded sx={{ fontSize: 42, color: '#a8b6c6', mb: 0.7 }} />
                <Typography fontWeight={900}>No hay clientes para mostrar</Typography>
                <Typography variant="body2" color="text.secondary">
                  Escriba un RUT o razón social para buscar.
                </Typography>
              </Box>
            </Box>
          ) : (
            <List disablePadding>
              {rows.map((cliente, index) => {
                const validation = validarClienteFactura(cliente);
                const active = index === highlightedIndex;
                const nombre = clienteNombre(cliente);
                const dir = validation.direccionTexto;
                const loadingRow = Number(selectingId) === Number(cliente.id);

                return (
                  <React.Fragment key={cliente.id || `${cliente.rut}-${index}`}>
                    <ListItemButton
                      selected={active}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onDoubleClick={() => validation.valido && seleccionar(cliente)}
                      sx={{ py: 1.15, px: 1.4, alignItems: 'flex-start' }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={0.8}>
                          <Typography variant="body2" fontWeight={950} noWrap>{nombre}</Typography>
                          {validation.valido ? (
                            <CheckCircleRounded sx={{ fontSize: 17, color: '#16a34a', flexShrink: 0 }} />
                          ) : (
                            <WarningAmberRounded sx={{ fontSize: 17, color: '#d97706', flexShrink: 0 }} />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.15 }}>
                          RUT {cliente.rut || '—'} · {cliente.giro || 'Sin giro'}
                        </Typography>
                        <Stack direction="row" spacing={0.45} alignItems="center" sx={{ mt: 0.35 }}>
                          <LocationOnOutlined sx={{ fontSize: 15, color: '#7b8da2' }} />
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {dir || 'Sin dirección de facturación'}
                          </Typography>
                        </Stack>
                        {!validation.valido ? (
                          <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 0.35, fontWeight: 800 }}>
                            Falta: {validation.faltantes.join(', ')}
                          </Typography>
                        ) : null}
                      </Box>

                      <Button
                        size="small"
                        variant={validation.valido ? 'contained' : 'outlined'}
                        disabled={!validation.valido || selectingId != null}
                        onClick={(event) => {
                          event.stopPropagation();
                          seleccionar(cliente);
                        }}
                        sx={{ ml: 1, minWidth: 92, borderRadius: 1.3 }}
                      >
                        {loadingRow ? <CircularProgress size={18} color="inherit" /> : 'Elegir'}
                      </Button>
                    </ListItemButton>
                    {index < rows.length - 1 ? <Divider /> : null}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.9 }}>
          ↑ ↓ para recorrer · Enter para seleccionar · los clientes incompletos no pueden usarse en Factura.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.4 }}>
        <Button onClick={onClose} disabled={selectingId != null}>Cancelar</Button>
        {actualValidation.valido ? (
          <Button variant="contained" onClick={() => onSelect?.(clienteActual)} disabled={selectingId != null}>
            Usar cliente actual
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
